import { memo, SyntheticEvent, useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  CheckboxProps,
  Divider,
  Form,
  FormField,
  FormGroup,
  Header,
  Icon,
  Input,
  Modal,
} from "semantic-ui-react";

import {
  MemberGroupNodeParams,
  PatientDetailMap,
  PickedState,
  SelectedMemberNode,
  SetProp,
} from "./MeCastType";
import PublishedDiagRuleNodeMemberSerializer from "@/issara-sdk/types/PublishedDiagRuleNodeMemberSerializer_apps_PRX";
import { BTN_ACTS } from "./sequence/MeCast";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

// Components
import TableHandleMemberNode from "./TableHandleMemberNode";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

type ModHandleMemberNodeProps = {
  open: boolean;
  onClose: () => void;
  // data
  selectedNode: SelectedMemberNode | null;
  memberNodeList: PublishedDiagRuleNodeMemberSerializer[];
  patientDetailMap: PatientDetailMap;
  changedMembers: Map<number, boolean>;
  hasChanges: boolean;
  // filter
  filter: MemberGroupNodeParams;
  latestRefresh: string;
  onChangeData: (e: SyntheticEvent | null, data: any) => void;
  onToggleShowInactive: (checked: boolean) => void;
  onSearch: () => void;
  onRefresh: () => void;
  onSave: () => void;
  // member change
  onMemberActiveChange: (memberId: number, active: boolean) => void;
  // infra
  setProp: SetProp;
} & Pick<PickedState, "buttonLoadCheck">;

export const DEFAULT_MEMBER_NODE_LIST_LIMIT = 10;

const ModHandleMemberNode = (props: ModHandleMemberNodeProps) => {
  const { selectedNode, filter, memberNodeList, patientDetailMap, changedMembers, hasChanges } =
    props;

  const [activity, setActivity] = useState<number>(1);

  const handleToggleInactive = (_: SyntheticEvent, data: CheckboxProps) => {
    props.onToggleShowInactive(!!data.checked);
  };

  useEffect(() => {
    setActivity(1)
  },[props.memberNodeList])

  const isShowInactiveChecked = filter.active === false;

  const memberNodeListSlice: PublishedDiagRuleNodeMemberSerializer[] = useMemo(() => {
    const start = (activity - 1) * DEFAULT_MEMBER_NODE_LIST_LIMIT;
    const end = activity * DEFAULT_MEMBER_NODE_LIST_LIMIT;

    return props.memberNodeList.slice(start, end);
  }, [props.memberNodeList, activity]);

  return (
    <Modal size="large" open={props.open} onClose={props.onClose} style={{ padding: "1rem" }}>
      <div>
        {/* --- Header --- */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Header as="h2" style={{ margin: 0 }}>
            {selectedNode?.group?.diag_rule_name || ""}
          </Header>
          <Icon
            name="close"
            color="red"
            size="large"
            onClick={props.onClose}
            style={{ cursor: "pointer" }}
          />
        </div>

        <Divider style={{ margin: "0.5rem 0" }} />

        {/* --- Group Name Info --- */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <Header as="h3" style={{ margin: 0 }}>
            Group Name:{" "}
            <span style={{ color: "var(--primary-theme-color)" }}>
              {selectedNode?.node?.node_name || ""}
            </span>
          </Header>
          <div>
            {props.buttonLoadCheck?.[`${BTN_ACTS.GET_MEMBER_NODE_LIST}`] ? (
              <Icon name="spinner" loading />
            ) : (
              <Icon name="redo" style={{ cursor: "pointer" }} onClick={props.onRefresh} />
            )}
            <label style={{ fontStyle: "italic" }}>Last Updated: {props.latestRefresh}</label>
          </div>
        </div>

        {/* --- Filter --- */}
        <Form>
          <FormGroup inline>
            <FormField width={3}>
              <div style={{ width: "100%" }}>
                <Input
                  name="hn"
                  fluid
                  placeholder="Search by Member ID"
                  value={filter.hn}
                  onChange={props.onChangeData}
                />
              </div>
            </FormField>
            <FormField width={3}>
              <div style={{ width: "100%" }}>
                <Input
                  name="name"
                  fluid
                  placeholder="Search by Name"
                  value={filter.name}
                  onChange={props.onChangeData}
                />
              </div>
            </FormField>
            <FormField>
              <div style={{ width: "100%" }}>
                <ButtonLoadCheck
                  setProp={props.setProp}
                  paramKey={BTN_ACTS.GET_MEMBER_NODE_LIST}
                  buttonLoadCheck={props.buttonLoadCheck?.[`${BTN_ACTS.GET_MEMBER_NODE_LIST}`]}
                  onClick={props.onSearch}
                  style={{
                    background: "var(--primary-theme-color)",
                    color: "white",
                  }}
                  title={
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignContent: "center",
                      }}
                    >
                      <label style={{ cursor: "pointer" }}>Search</label>
                      <Icon name="search" />
                    </div>
                  }
                />
              </div>
            </FormField>
            <FormField>
              <div style={{ width: "100%" }}>
                <Checkbox
                  checked={isShowInactiveChecked}
                  onChange={handleToggleInactive}
                  label={
                    <label style={{ color: "var(--primary-theme-color)" }}>
                      Show only inactive member(s)
                    </label>
                  }
                />
              </div>
            </FormField>
            <FormField style={{ marginLeft: "auto", padding: 0 }}>
              <div style={{ width: "100%" }}>
                <ButtonLoadCheck
                  setProp={props.setProp}
                  paramKey={BTN_ACTS.ACTION_MEMBER_NODE}
                  buttonLoadCheck={props.buttonLoadCheck?.[`${BTN_ACTS.ACTION_MEMBER_NODE}`]}
                  onClick={props.onSave}
                  disabled={!hasChanges}
                  style={{
                    background: "var(--primary-theme-color-dark)",
                    color: "white",
                  }}
                  title={
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignContent: "center",
                      }}
                    >
                      <Icon name="save outline" />
                      <label style={{ cursor: "pointer" }}>
                        Save {hasChanges && `(${changedMembers.size})`}
                      </label>
                    </div>
                  }
                />
              </div>
            </FormField>
          </FormGroup>
        </Form>

        {/* --- Table --- */}
        <TableHandleMemberNode
          memberNodeList={memberNodeListSlice || []}
          patientDetailMap={patientDetailMap}
          changedMembers={changedMembers}
          onMemberActiveChange={props.onMemberActiveChange}
        />

        {/* --- Pagination --- */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
          <PaginationShorthand
            activePage={activity || 1}
            limit={DEFAULT_MEMBER_NODE_LIST_LIMIT}
            total={(props.memberNodeList || []).length || 0}
            onPageChange={(activePage) => setActivity(activePage)}
          />
        </div>
      </div>
    </Modal>
  );
};

export default memo(ModHandleMemberNode);